
import { ApplicationUserModel, ProductModel, ProductCollectionModel } from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Ref, Watch } from 'vue-property-decorator';
import { applicationUserStore } from '@/libs/user-settings/+state/store';
import { Guid } from '@/libs/common/functions/guid';
import { productsStore, productCollectionsStore } from '@/libs/products/+state/store';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import ProjectSelect from '@/libs/common/components-smart/ProjectSelect.vue';
import ProductScrapReasonList from '@/libs/settings/components/ProductScrapReasonList.vue';
import ProductBillOfMaterialList from '@/libs/settings/components/ProductBillOfMaterialList.vue';

@Component({
    components: { ProductScrapReasonList, ProjectSelect, ProductBillOfMaterialList },
})
export default class ProductDialog extends Page {
    id!: string;
    dialog = false;
    valid = true;
    saving = false;
    loading = false;
    applicationUser: ApplicationUserModel = applicationUserStore.useGetter(CrudGetter.Detail) as ApplicationUserModel;
    product: ProductModel | null = null;
    deleteDialog = false;
    deleting = false;

    @Ref() form!: any;

    get isNewItem(): boolean {
        return this.product?.id == Guid.EmptyGuid();
    }

    open(id: string) {
        this.dialog = true;
        this.id = id;
        this.load();
    }

    close() {
        this.dialog = false;
    }

    private load(): Promise<any> {
        if (this.id != Guid.EmptyGuid()) {
            this.loading = true;
            return new Promise((resolve, reject) => {
                this.loadProductCollections()
                    .then(() => this.loadProduct())
                    .then((e) => {
                        this.loading = false;
                        resolve(e);
                    })
                    .catch((e: any) => {
                        reject(e);
                    });
            });
        } else {
            this.product = {
                id: Guid.EmptyGuid(),
                code: '',
                name: '',
                projectId: undefined,
                productScrapReasons: [],
                billOfMaterialItems: [],
            } as ProductModel;
            return this.loadProductCollections().then(() => Promise.resolve(null));
        }
    }

    private loadProduct() {
        return new Promise((resolve, reject) => {
            productsStore.dispatch(CrudAction.Get, { id: this.id });
            this.subscribe(productsStore, CrudReponse.Get)
                .then((e) => {
                    this.product = e;
                    resolve(e);
                })
                .catch((e: any) => {
                    reject(e);
                });
        });
    }

    private loadProductCollections(): Promise<any> {
        return new Promise((resolve, reject) => {
            productCollectionsStore.dispatch(
                CrudAction.GetAll,
                createCrudQueryPayload<ProductCollectionModel>([{ field: 'name', index: 1, order: 'asc' }])
            );
            this.subscribe(productCollectionsStore, CrudReponse.GetAll)
                .then(() => resolve(null))
                .catch((e: any) => reject(e));
        });
    }

    get productCollections() {
        const items = (productCollectionsStore.useGetter(CrudGetter.Data) as any[]) ?? [];
        return items.map((x) => ({ id: x.id, label: `${x.name ?? ''}`.trim() })).sort((a, b) => a.label.localeCompare(b.label));
    }

    private save() {
        if (this.form.validate()) {
            const product = { ...this.product };
            this.saving = true;
            if (this.id == Guid.EmptyGuid()) {
                productsStore.dispatch(CrudAction.Create, { item: product });
                this.subscribe(productsStore, CrudReponse.Create)
                    .then(() => {
                        this.saving = false;
                        this.close();
                        this.$emit('saved');
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            } else {
                productsStore.dispatch(CrudAction.Update, { item: product });
                this.subscribe(productsStore, CrudReponse.Update)
                    .then(() => {
                        this.saving = false;
                        this.close();
                        this.$emit('saved');
                    })
                    .catch((e) => {
                        this.saving = false;
                        throw e;
                    });
            }
        }
    }

    deleteClick() {
        this.deleteDialog = true;
    }

    deleteProduct() {
        this.deleting = true;
        productsStore.dispatch(CrudAction.Delete, { id: this.id });
        this.subscribe(productsStore, CrudReponse.Delete)
            .then((e: any) => {
                this.deleting = false;
                this.deleteDialog = false;
                this.close();
            })
            .catch((e: any) => {
                this.deleting = false;
            });
    }
}
